"use client";

import { IDashboardUserBadge, IUserCertificate } from "@/api";
import {
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Pagination,
  PaginationItem,
  CircularProgress,
  Link,
  Button,
  CardContent,
  Card,
  CardMedia,
  Menu,
  MenuItem,
} from "@mui/material";
import { apiRemote } from "@/api/wp-api";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowBackIos,
  ArrowForward,
  ArrowForwardIos,
} from "@mui/icons-material";
import { useMyCertificateContext } from "./Layout";
import { GridColDef } from "@mui/x-data-grid";
import { MenuIcon } from "lucide-react";
import ListingTableTemplate from "@/components/ListingTableTemplate";
import OpenCertDialog from "./RequestCertificateDialog";

export interface IUserCertificateExtend extends IUserCertificate {}

export default function MyCertificate() {
    const [rows, setRows] = useState<IUserCertificate[]>([]);
    const [initialData, setInitialData] = useState<any>(null);
    const [count, setCount] = useState(0);
    const [userCert, setUserCert] = useState<IUserCertificate | null>(null);
    const { getUserCertificate } = useMyCertificateContext();
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);

    const [menuState, setMenuState] = useState<{ [key: number]: HTMLElement | null }>({});

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, rowId: number) => {
        setMenuState((prev) => ({ ...prev, [rowId]: event.currentTarget }));
    };

    const handleMenuClose = (rowId: number) => {
        setMenuState((prev) => ({ ...prev, [rowId]: null }));
    };
    const reloadData = useCallback(() => {
        setLoading(true);
        getUserCertificate().then((data) => {
            setRows(data['data']);
            setLoading(false);
        });
    }, [count]);

    useEffect(() => {
        reloadData();
    }, []);

    const handleDownload = (fileUrl: any, fileName: any) => {
        fetch(fileUrl)
            .then((response) => response.blob())
            .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            })
            .catch((error) => {
                console.error('Download failed:', error);
            });
    };
    const columns: GridColDef[] = [
        { field: 'module_id', headerName: 'Module ID', flex: 1 },
        { field: 'cer_name', headerName: 'Name', flex: 1 },
        {
            field: ' ', headerName: ' ', sortable: false, filterable: false, 
            disableColumnMenu: true,
            
            renderCell: (params) => {
                const status = params.row.order_status;
                const rowId = params.row.module_id;
                const isMenuOpen = !!menuState[rowId];

                const menuItems = (() => {
                if (status === 1) {
                    return [
                    <MenuItem key="view" onClick={() => window.open(params.row.cer_link, 'CertificatePreview', 'width=800,height=600,resizable=yes,scrollbars=yes')}>
                        View
                    </MenuItem>,
                    <MenuItem
                        key="download"
                        onClick={() => handleDownload(params.row.oa_file_url, params.row.oa_file_name ?? "")}
                    >
                        Download OpenCert
                    </MenuItem>,
                    ];
                } else if (status === 3) {
                    return [
                    <MenuItem key="view" onClick={() => window.open(params.row.cer_link, 'CertificatePreview', 'width=800,height=600,resizable=yes,scrollbars=yes')}>
                        View
                    </MenuItem>,
                    <MenuItem key="request-sent" disabled style={{ cursor: "not-allowed" }}>
                        Request Sent
                    </MenuItem>,
                    ];
                } else {
                    return [
                    <MenuItem key="view" onClick={() => window.open(params.row.cer_link, 'CertificatePreview', 'width=800,height=600,resizable=yes,scrollbars=yes')}>
                        View
                    </MenuItem>,
                    <MenuItem key="request" onClick={() => {
                        setInitialData({ 
                            module_id: params.row.module_id,
                            cer_name: params.row.cer_name,
                            cer_link: params.row.cer_link,
                            icon_url: params.row.icon_url,
                          });
                        setOpenDialog(true)
                    }}>
                        Request for OpenCert
                    </MenuItem>,
                    ];
                }
                })();

                return (
                <>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={(e) => handleMenuOpen(e, rowId)}
                    >
                        <MenuIcon />
                    </Button>
                    <Menu
                        anchorEl={menuState[rowId]}
                        open={isMenuOpen}
                        onClose={() => handleMenuClose(rowId)}
                        MenuListProps={{
                            "aria-labelledby": "basic-button",
                        }}
                    >
                        {menuItems}
                    </Menu>
                </>
                );
            }
        }
    ]

    return (
        <Box
        sx={{
            p: 3,
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            flex: 1,
        }}
        >
            <ListingTableTemplate
                // checkboxSelection={true}
                disableRowSelectionOnClick={true}
                buttonOptions={[
                    { label: 'Reload data' },
                ]}
                pageTitle="My Certificates"
                columns={columns}
                rows={rows}
                loading={loading}
                onMenuClick={reloadData}
                sortModel={[{ field: 'cer_name', sort: 'asc' }]}
                getRowId={(row: any) => row.module_id} 
            />
            <OpenCertDialog open={openDialog} onClose={()=>{setOpenDialog(false)}} initialData={initialData} reload={reloadData}/>
        </Box>
        
        
    );
}